import type { Dispatch, SetStateAction } from "react";
import { mapEnum } from "@/utils/strings";
import {
  CreateQuestionnaireQuestionSettings,
  ConvertToContractData,
  DealCloseData,
  DealColumn,
  DealConfidenceLevel,
  CsmRenewalConfidenceLevel,
  DealContractDuration,
  DealData,
  DealQuestionnaireQuestionSettings,
  DealStatus
} from "./types";
import { TFunction } from "i18next";
import { APILookupValue, Filters as APIFilters } from "@/services/api/types";
import { camelCase } from "lodash";
import { ISOStringFormat } from "date-fns";
import { toLocalDate, toDateOnlyISO } from "@/utils/datetime";
import { formatNumber } from "@/utils/numbers";
import { getCurrentLocale } from "@/utils/locale";
import CurrencyList from "currency-list";
import { Product } from "@/pages/settings/components/settingsItems/hooks/types";
import { ContractData } from "@/pages/contracts/types";
import { DATE_BUCKET_SEPARATOR } from "@/entities/dataTables/monthGrouping";

export const dealsColumnKeys = Object.values(DealColumn);
export const dealStatuses = mapEnum(DealStatus, (value) => value);
export const dealOpenStatuses = dealStatuses.filter(
  (status) =>
    status !== DealStatus.CLOSED_LOST && status !== DealStatus.CLOSED_WON
);
export const dealConfidenceLevels = mapEnum(
  DealConfidenceLevel,
  (value) => value
);
export const csmRenewalConfidenceLevels = mapEnum(
  CsmRenewalConfidenceLevel,
  (value) => value
);

export interface ResolvedClosedWonContract {
  // False while the contract fetch is in flight or the enable→fetch tick
  // leaves `contract` transiently undefined — until then a snapshot can't be
  // told apart from a live contract, so any target derived from it is unsafe.
  isResolved: boolean;
  // Undefined dealContractId, an unresolved fetch, or a snapshot with no
  // surviving live contract all collapse to undefined here.
  liveContractId: string | undefined;
}

// A snapshot contract (deals.contractId after closed-won) is a frozen T&S
// container, not a page a user should land on — every consumer needing a
// navigable contract id must resolve through the live contract it was
// duplicated from instead of using the snapshot id directly.
export const resolveClosedWonContract = (
  dealContractId: string | null | undefined,
  contract: Pick<
    ContractData,
    "snapshotOfDealId" | "duplicatedFromContractId"
  > | null
    | undefined,
  isContractLoading: boolean
): ResolvedClosedWonContract => {
  const isResolved = !dealContractId || (!isContractLoading && Boolean(contract));
  if (!isResolved) {
    return { isResolved: false, liveContractId: undefined };
  }
  const contractIsSnapshot = Boolean(contract?.snapshotOfDealId);
  const liveContractId = contractIsSnapshot
    ? (contract?.duplicatedFromContractId ?? undefined)
    : (dealContractId ?? undefined);
  return { isResolved: true, liveContractId };
};

export const dealStatusEnumToString = (enumValue: DealStatus): string =>
  DealStatus[enumValue];
export const dealStatusStringToEnum = (value: string): DealStatus =>
  DealStatus[value as keyof typeof DealStatus];
export const dealConfidenceLevelEnumToString = (
  enumValue: DealConfidenceLevel | CsmRenewalConfidenceLevel
): string => DealConfidenceLevel[enumValue];
export const dealConfidenceLevelStringToEnum = (
  value: string
): DealConfidenceLevel =>
  DealConfidenceLevel[value as keyof typeof DealConfidenceLevel];

export const dealContractDurations = mapEnum(
  DealContractDuration,
  (value) => value
);

export const getOpenDeals = (deals: DealData[]) =>
  deals.filter(
    (deal) =>
      ![DealStatus.CLOSED_LOST, DealStatus.CLOSED_WON].includes(
        deal.status as DealStatus
      )
  );

export const closedLostReasonsFromIdx = (
  t: TFunction,
  i18n: {
    exists: (key: string) => boolean;
  },
  closedLostReasons: APILookupValue[],
  value: number
): string => {
  const reason = closedLostReasons.find((i) => i.id === value);
  if (reason) {
    const key = `deals.modal.lostReasons.${camelCase(reason.name)}`;
    return i18n.exists(key) ? t(key) : reason.name;
  }
  return t("tables.notAvailable");
};

export const emptyQuestion: CreateQuestionnaireQuestionSettings = {
  questionnaireId: "",
  responseType: {
    type: null,
    setValueType: null,
    unit: null,
    value: ""
  },
  isSystem: false,
  isMandatory: false,
  isSuccessCriteria: true,
  section: null,
  fieldName: null,
  questionText: "",
  tooltipText: null,
  questionOrder: null,
  relatedQuestionId: null,
  filledByDreamer: null
};

export const calculateContractEndDate = (
  // "" is the sentinel DateSelection's clear action sends through
  // ContractTerms' onChange; it is a real input, not a type escape.
  startDate: ISOStringFormat | "" | null | undefined,
  duration: DealContractDuration
) => {
  if (!startDate) {
    return null;
  }
  const date = toLocalDate(startDate);
  switch (duration) {
    case DealContractDuration.ONE_MONTH:
      date.setMonth(date.getMonth() + 1);
      break;
    case DealContractDuration.ONE_QUARTER:
      date.setMonth(date.getMonth() + 3);
      break;
    case DealContractDuration.ONE_YEAR:
      date.setFullYear(date.getFullYear() + 1);
      break;
    case DealContractDuration.TWO_YEARS:
      date.setFullYear(date.getFullYear() + 2);
      break;
    case DealContractDuration.THREE_YEARS:
      date.setFullYear(date.getFullYear() + 3);
      break;
    default:
      return null;
  }
  return toDateOnlyISO(date);
};

/**
 * What a start-date edit should write to `contractEndDate`, or `undefined` to
 * leave it alone.
 *
 * The end date belongs to this field only while a duration derives it. Under
 * OTHER (or no duration) it is a value the user typed, so a new start date must
 * not touch it and clearing the start date must not delete it — nothing derived
 * it. Within the derived case `calculateContractEndDate` answers `null` for a
 * cleared start date, which is what clears the end date alongside it.
 *
 * A single function because ContractTerms has two paths to this decision, one
 * behind the breakdown confirmation modal and one not. They were written
 * separately and disagreed: the breakdown path sent `contractEndDate: null` on
 * every start-date edit, so picking a new start date under OTHER deleted a
 * custom end date the user had entered.
 */
export const contractEndDateFieldsForStartDate = (
  startDate: ISOStringFormat | "" | null | undefined,
  duration: DealContractDuration | null
): { contractEndDate: string | null } | undefined =>
  duration === null || duration === DealContractDuration.OTHER
    ? undefined
    : { contractEndDate: calculateContractEndDate(startDate, duration) };

export const determineDealContractDuration = (
  startDate: ISOStringFormat | null | undefined,
  endDate: ISOStringFormat | null | undefined
): DealContractDuration | null => {
  if (!startDate && !endDate) {
    return null;
  }
  if (!startDate && endDate) {
    return DealContractDuration.OTHER;
  }

  const start = toLocalDate(startDate as string);
  const end = toLocalDate(endDate as string);

  const oneMonthDate = new Date(start);
  oneMonthDate.setMonth(oneMonthDate.getMonth() + 1);

  const oneQuarterDate = new Date(start);
  oneQuarterDate.setMonth(oneQuarterDate.getMonth() + 3);

  const oneYearDate = new Date(start);
  oneYearDate.setFullYear(oneYearDate.getFullYear() + 1);

  const twoYearsDate = new Date(start);
  twoYearsDate.setFullYear(twoYearsDate.getFullYear() + 2);

  const threeYearsDate = new Date(start);
  threeYearsDate.setFullYear(threeYearsDate.getFullYear() + 3);

  const normalizeDate = (date: Date): number => {
    const normalized = new Date(date);
    normalized.setHours(0, 0, 0, 0);
    return normalized.getTime();
  };

  const endTime = normalizeDate(end);
  const oneMonthTime = normalizeDate(oneMonthDate);
  const oneQuarterTime = normalizeDate(oneQuarterDate);
  const oneYearTime = normalizeDate(oneYearDate);
  const twoYearsTime = normalizeDate(twoYearsDate);
  const threeYearsTime = normalizeDate(threeYearsDate);

  // One day in ms
  const ONE_DAY = 86400000;

  if (Math.abs(endTime - oneMonthTime) < ONE_DAY) {
    return DealContractDuration.ONE_MONTH;
  } else if (Math.abs(endTime - oneQuarterTime) < ONE_DAY) {
    return DealContractDuration.ONE_QUARTER;
  } else if (Math.abs(endTime - oneYearTime) < ONE_DAY) {
    return DealContractDuration.ONE_YEAR;
  } else if (Math.abs(endTime - twoYearsTime) < ONE_DAY) {
    return DealContractDuration.TWO_YEARS;
  } else if (Math.abs(endTime - threeYearsTime) < ONE_DAY) {
    return DealContractDuration.THREE_YEARS;
  } else {
    return DealContractDuration.OTHER;
  }
};

export const getCurrencySymbol = (currency: string) =>
  CurrencyList.get(currency)?.symbol_native;

export const convertCurrency = (rate: number, amount: number) => {
  const convertedAmount = rate * amount;
  const { value: formattedAmount } = formatNumber(
    convertedAmount,
    0,
    true,
    false,
    false,
    getCurrentLocale()
  );
  return formattedAmount;
};

export const monetaryValueFormatter = (
  value: number,
  contractCurrencySymbol: string
) => {
  const { value: formattedValue } = formatNumber(
    value,
    0,
    true,
    false,
    false,
    getCurrentLocale()
  );
  return `${contractCurrencySymbol}${formattedValue}`;
};

export const monetaryValueFormatterWithDecimals = (
  value: number,
  currencySymbol: string,
  decimals: number = 2
) => {
  const { value: formattedValue } = formatNumber(
    value,
    decimals,
    false,
    false,
    false,
    getCurrentLocale()
  );
  return `${currencySymbol}${formattedValue}`;
};

export const prettyNumberValueFormatter = (value: number) => {
  const { value: formattedValue } = formatNumber(
    value,
    0,
    true,
    false,
    false,
    getCurrentLocale(),
    true
  );
  return formattedValue;
};

export const getProductNameById = (
  productId: string | null,
  products: Product[] | undefined
) => {
  if (!productId || !products) return;
  const product = products?.find((pr) => pr.id === productId);
  return product ? product?.name : undefined;
};

export const createDealClosePayload = (
  dealId: string,
  status: DealStatus,
  data?: DealCloseData,
  contractData?: ConvertToContractData
) => ({
  pathParams: { dealId },
  data: {
    newStatus: status as DealStatus,
    closedLostReason: data?.reason,
    closedLostReasonText: data?.comment || null,
    ...(contractData && {
      contractName: contractData.contractName,
      companyId: contractData.companyId,
      productId: contractData.productId,
      currency: contractData.currency,
      contractSignDate: contractData.contractSignDate || null,
      aeOwner: contractData.aeOwner || null,
      csmOwner: contractData.csmOwner || null
    })
  }
});

export const removeQuestionById = (
  questions: DealQuestionnaireQuestionSettings[] | null | undefined,
  questionId: string
): DealQuestionnaireQuestionSettings[] => {
  const questionsArray = questions || [];
  const index = questionsArray.findIndex(
    (question) => question?.id === questionId
  );

  if (index !== -1) {
    const newQuestions = [...questionsArray];
    newQuestions.splice(index, 1);
    return newQuestions;
  }

  return questionsArray;
};

// Deals columns whose id is not a field the API can sort on: closing quarter is
// derived from the expected close date, and the TCV and ACV columns keep their
// saved-layout ids while reading the contract rollups. Shared by the table and
// the export so a CSV comes out in the order shown on screen.
//
// The ARR/MRR column needs no entry: it takes its accessor key from the account's
// arr-or-mrr setting, so its column id is already the field name the API sorts on.
export const DEALS_SORT_FIELD_BY_COLUMN: Record<string, string> = {
  [DealColumn.CLOSING_QUARTER]: DealColumn.EXPECTED_CLOSE_AT,
  [DealColumn.CONTRACTS_TCV]: "contractsTotalContractValue",
  [DealColumn.CONTRACTS_ACV]: "contractsAnnualContractValue"
};

// Group options that were replaced, mapped to the option that took their place, so
// a link or view still grouped by the old one keeps the same grouping.
const LEGACY_DEAL_GROUP_BY_ALIASES: Readonly<Record<string, string>> = {
  [DealColumn.CLOSING_QUARTER]: `${DealColumn.EXPECTED_CLOSE_AT}${DATE_BUCKET_SEPARATOR}quarter`
};

export const normalizeDealsGroupBy = (groupBy: string[]): string[] =>
  groupBy.map((id) => LEGACY_DEAL_GROUP_BY_ALIASES[id] ?? id);

/**
 * The filters as the deals API reads them. Closing quarter is derived from the
 * expected close date and has no field of its own, so its date filter is sent as
 * a second expected close date condition — under `$and`, so an Expected close
 * filter set alongside it still applies too.
 */
export const toDealsRequestFilters = <F extends APIFilters>(filters: F): F => {
  const { [DealColumn.CLOSING_QUARTER]: closingQuarter, ...rest } = filters;
  if (closingQuarter === undefined) return filters;
  const existingAnd = (rest as { $and?: unknown[] }).$and ?? [];
  return {
    ...rest,
    $and: [
      ...existingAnd,
      { [DealColumn.EXPECTED_CLOSE_AT]: closingQuarter }
    ]
  } as unknown as F;
};

/**
 * Filters for a single kanban column's deals query.
 *
 * The column's own stage is authoritative: a toolbar stage filter narrows which
 * columns hold deals, it never widens a column's query to other stages. Spreading
 * the toolbar filters over `stage` instead makes every column fetch the same
 * multi-stage result set, and each column then contributes its own copy of the
 * same deal to the shared board state.
 */
export const buildKanbanColumnFilters = <F extends { stage?: number[] }>(
  filters: F,
  columnId: number
): F & { status: DealStatus[]; stage: number[] } => ({
  status: dealOpenStatuses,
  ...filters,
  stage: [columnId]
});

/**
 * Whether a column should query at all: a toolbar stage filter that excludes this
 * column means the column is empty, not that it falls back to every stage.
 */
export const isStageIncludedInFilter = (
  stageFilter: number[] | undefined,
  columnId: number
): boolean =>
  !Array.isArray(stageFilter) ||
  stageFilter.length === 0 ||
  stageFilter.includes(columnId);

/**
 * Replaces one column's deals inside the board's shared deal list. Deals are
 * matched out by id as well as by stage so a deal that arrives under a different
 * stage than the one it was last stored under cannot be held twice.
 */
export const mergeColumnDealsIntoState = (
  previousDeals: DealData[],
  columnDeals: DealData[],
  columnId: number
): DealData[] => {
  const incomingIds = new Set(columnDeals.map((deal) => deal.id));
  const dealsFromOtherColumns = previousDeals.filter(
    (deal) => deal.stage !== columnId && !incomingIds.has(deal.id)
  );
  return [...dealsFromOtherColumns, ...columnDeals];
};

/**
 * Takes a deal off the board immediately and returns the function that puts it
 * back where it was, for a change that should look done before the server
 * answers. Putting it back is a no-op once a column refetch has re-added it.
 */
export const removeDealFromBoard = (
  deals: DealData[],
  setDeals: Dispatch<SetStateAction<DealData[]>>,
  dealId: string
): (() => void) => {
  const index = deals.findIndex((deal) => deal.id === dealId);
  if (index === -1) return () => {};
  const removedDeal = deals[index];

  setDeals((previousDeals) =>
    previousDeals.filter((deal) => deal.id !== dealId)
  );

  return () =>
    setDeals((previousDeals) =>
      previousDeals.some((deal) => deal.id === dealId)
        ? previousDeals
        : [
            ...previousDeals.slice(0, index),
            removedDeal,
            ...previousDeals.slice(index)
          ]
    );
};

interface DealColumnSeedInputs {
  hasCustomFields: boolean;
  isAccountSettingsLoading: boolean;
  currencyUsageResolved: boolean;
  arrMrrResolved: boolean;
}

// Named fields, not positional: four booleans would mis-order without a type error.
export const canSeedDealColumns = ({
  hasCustomFields,
  isAccountSettingsLoading,
  currencyUsageResolved,
  arrMrrResolved
}: DealColumnSeedInputs): boolean =>
  hasCustomFields &&
  !isAccountSettingsLoading &&
  currencyUsageResolved &&
  arrMrrResolved;
